/**
 * Parent/child session tree for the Inspector.
 *
 * Before this, a parent chat and its spawned subagents rendered as peer cards:
 * no nesting, no ownership indicator, and selecting a parent left its children
 * looking like unrelated runs. The operator could not tell which children
 * belonged to the session they were looking at.
 *
 * `ChatSubagentCard.parentSessionKey` already carries the edge (it is set by
 * `syncCardsFromRegistry` from the registry's `requesterSessionKey`, and by
 * `cardsFromSpawnedSessions` from the session row's `spawnedBy`), so the tree
 * is derived rather than stored. A card without a parent key is a root — that
 * is the normal case for cards rendered inside their own session's view.
 */
import type { ChatSubagentCard } from "../ui-types.ts";
import { resolveSubagentActivityPhase } from "./subagent-run-status.ts";

/** One node of the rendered tree. */
export type SessionTreeNode = {
  card: ChatSubagentCard;
  /** 0 for a parent session, 1 for its direct children, and so on. */
  depth: number;
  /**
   * Session key of the owning parent, or undefined for a root. Rendered on
   * the card so ownership is legible without relying on indentation alone
   * (indentation is lost in the flat status table and in copied text).
   */
  parentSessionKey?: string;
};

/** A parent session and the children spawned from it. */
export type SessionTreeGroup = {
  parentSessionKey: string;
  /** The parent itself, when it was present in the input card set. */
  parent?: ChatSubagentCard;
  children: ChatSubagentCard[];
  /**
   * True when this group is the session the operator is currently viewing.
   * Children of the active parent render highlighted so the relationship
   * survives a click elsewhere in the Inspector.
   */
  isActiveParent: boolean;
};

export type SessionTree = {
  groups: SessionTreeGroup[];
  /** Flat depth-annotated list in render order (parents before children). */
  nodes: SessionTreeNode[];
  /** Total children across all groups. */
  childCount: number;
};

/** Short display form of a session key: the last two `:` segments. */
export function shortSessionKey(sessionKey: string): string {
  const parts = sessionKey.split(":").filter(Boolean);
  if (parts.length <= 2) return sessionKey;
  return parts.slice(-2).join(":");
}

/**
 * True when `card` was spawned by `parentKey`.
 *
 * Compares against both `parentSessionKey` and the registry-derived key the
 * card was restored with. A card is its own child only if the key comparison
 * says so; an empty or missing parent never matches, which keeps roots out of
 * the grouping path.
 */
export function isChildOf(card: ChatSubagentCard, parentKey: string): boolean {
  const parent = card.parentSessionKey?.trim();
  return Boolean(parent) && parent === parentKey;
}

/**
 * Group children under the parent session that spawned them.
 *
 * `activeParentKey` is the session currently open in chat. Groups are ordered
 * active-parent-first, then by most-recent child, so the session the operator
 * is working in stays at the top of the tab.
 *
 * Cards that name a parent absent from `parentKeys` are still grouped under
 * that parent (their owner is known even if the parent card is not in this
 * set) — otherwise rehydrated children would lose their ownership the moment
 * the parent card was filtered out.
 */
export function buildSessionTree(
  cards: ChatSubagentCard[],
  opts: { activeParentKey?: string; parentKeys?: string[] } = {},
): SessionTree {
  const activeParentKey = opts.activeParentKey?.trim() ?? "";
  const parentKeys = new Set((opts.parentKeys ?? []).map((k) => k.trim()).filter(Boolean));

  const byParent = new Map<string, ChatSubagentCard[]>();
  const roots: ChatSubagentCard[] = [];
  const seenChildKeys = new Set<string>();

  for (const card of cards) {
    const parentKey = card.parentSessionKey?.trim();
    if (!parentKey) {
      roots.push(card);
      continue;
    }
    // Defensive: a registry row whose parent is itself would otherwise make
    // the tree infinitely deep and render a card under itself.
    if (parentKey === card.childSessionKey) {
      roots.push(card);
      continue;
    }
    const list = byParent.get(parentKey) ?? [];
    list.push(card);
    byParent.set(parentKey, list);
    seenChildKeys.add(card.childSessionKey);
  }

  // Parent session keys worth rendering a group for: explicitly supplied,
  // plus any key that is actually a parent of something in this set.
  const candidateParents = new Set<string>(parentKeys);
  for (const key of byParent.keys()) {
    if (key === activeParentKey) {
      candidateParents.add(key);
      continue;
    }
    if (seenChildKeys.has(key)) {
      candidateParents.add(key);
    } else if (parentKeys.size === 0) {
      // No parent context supplied — treat every parent key as renderable so
      // the tab still shows ownership instead of a flat list.
      candidateParents.add(key);
    }
  }

  const byKey = new Map(cards.map((c) => [c.childSessionKey, c]));

  const groups: SessionTreeGroup[] = [];
  for (const parentKey of candidateParents) {
    const children = byParent.get(parentKey);
    if (!children || children.length === 0) continue;
    const sorted = [...children].sort((a, b) => a.createdAt - b.createdAt);
    groups.push({
      parentSessionKey: parentKey,
      parent: byKey.get(parentKey),
      children: sorted,
      isActiveParent: parentKey === activeParentKey,
    });
  }

  // Active parent first, then most recently active child, then by key so the
  // order is stable across renders (an unstable order makes the tab flicker
  // on every status update).
  groups.sort((a, b) => {
    if (a.isActiveParent !== b.isActiveParent) return a.isActiveParent ? -1 : 1;
    const aMax = Math.max(...a.children.map((c) => c.createdAt), 0);
    const bMax = Math.max(...b.children.map((c) => c.createdAt), 0);
    if (aMax !== bMax) return bMax - aMax;
    return a.parentSessionKey.localeCompare(b.parentSessionKey);
  });

  const nodes: SessionTreeNode[] = [];
  for (const group of groups) {
    if (group.parent) {
      nodes.push({ card: group.parent, depth: 0 });
    }
    for (const child of group.children) {
      nodes.push({ card: child, depth: 1, parentSessionKey: group.parentSessionKey });
    }
  }

  return {
    groups,
    nodes,
    childCount: groups.reduce((sum, g) => sum + g.children.length, 0),
  };
}

/**
 * Children of `parentKey`, in render order. Used to highlight the active
 * parent's subtree when the operator selects a different child.
 */
export function childrenOfParent(cards: ChatSubagentCard[], parentKey: string): ChatSubagentCard[] {
  return cards.filter((c) => isChildOf(c, parentKey));
}

/**
 * One-line, operator-facing description of what a child was asked to do and
 * how it went. Replaces the raw `agentmainfork` / `sessionKey` log lines in
 * the Inspector: "what it was asked, current status, key finding if available".
 */
export function describeSubagentRun(card: ChatSubagentCard): {
  asked: string;
  status: string;
  finding: string;
} {
  const asked = card.task?.trim() || card.title?.trim() || "No task recorded";
  const phase = resolveSubagentActivityPhase(card);
  const status =
    card.phase?.trim() ||
    card.toolHint?.trim() ||
    (phase === "ongoing" ? "Working" : phase === "completed" ? "Finished" : phase);

  let finding = "";
  if (phase === "failed" || phase === "defunct") {
    finding = card.error?.trim() || "No error detail recorded";
  } else if (phase === "cancelled") {
    finding = "Stopped by operator";
  } else {
    const preview = card.outputPreview?.trim() || card.streamText?.trim();
    if (preview) {
      // Key finding, not a wall of text — the full output stays behind the
      // card's expand toggle and the Debug section.
      const oneLine = preview.replace(/\s+/g, " ").trim();
      finding = oneLine.length > 180 ? `${oneLine.slice(0, 180)}…` : oneLine;
    }
  }

  return { asked, status, finding };
}
